<template>
  <div
    class="chart-layout"
    :class="{ 'legend-top': useTopLegend }"
  >
    <div
      v-if="showControls"
      class="controls"
    >
      <button
        class="legend-btn"
        @click="showAll"
      >
        Show All
      </button>
      <button
        class="legend-btn"
        @click="hideAll"
      >
        Hide All
      </button>
    </div>
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import Chart from "chart.js/auto";

export default {
  name: "SteelCaseGunsEqualizationChart",
  props: {
    datasets: { type: Array, required: true },
    xTitle: { type: String, default: "Powder Amount Setting" },
    yTitle: { type: String, default: "Throughput (Powder per minute)" },
    recipes: { type: Array, default: () => [] },
    powderOutputMeasurements: { type: Array, default: () => [] },
    showControls: { type: Boolean, default: true },
  },
  setup(props) {
    const chartCanvas = ref(null);
    let chartInstance = null;
    const useTopLegend = ref(false);

    const colors = [
      "#3A416F",
      "#17c1e8",
      "#f64e60",
      "#f1b44c",
      "#20c997",
      "#5c7cfa",
      "#6c757d",
      "#495057",
      "#dee2e6",
      "#ff6f61",
      "#6b5b95",
      "#88b04b",
      "#d65076",
      "#45b8ac",
      "#e94b3c",
      "#6b5b95",
    ];

    const drawChart = () => {
      if (!chartCanvas.value || !props.datasets.length) return;
      if (chartInstance) chartInstance.destroy();

      useTopLegend.value = props.datasets.length > 10;

      const ctx = chartCanvas.value.getContext("2d");
      const gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
      gradientStroke.addColorStop(1, "rgba(20,23,39,0.2)");
      gradientStroke.addColorStop(0.2, "rgba(72,72,176,0.0)");
      gradientStroke.addColorStop(0, "rgba(20,23,39,0)");

      const baseDatasets = props.datasets.map((dataset, index) => ({
        label: dataset.label,
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 0,
        borderColor: colors[index % colors.length],
        backgroundColor: gradientStroke,
        data: dataset.data.map(point => ({ ...point, y: point.y })),
        parsing: { xAxisKey: "x", yAxisKey: "y" },
      }));

      const indicatorDatasets = [];

      if (props.recipes.length) {
        props.recipes.forEach((recipe, index) => {
          const color = colors[index % colors.length];
          if (recipe.target_throughput) {
            indicatorDatasets.push({
              label: `Target ${recipe.category}`,
              type: "line",
              data: [
                { x: Math.min(...props.datasets.flatMap(ds => ds.data.map(p => p.x))), y: recipe.target_throughput },
                { x: Math.max(...props.datasets.flatMap(ds => ds.data.map(p => p.x))), y: recipe.target_throughput },
              ],
              borderColor: color,
              borderWidth: 2,
              borderDash: [8, 4],
              pointRadius: 0,
              fill: false,
            });
          }
          if (recipe.target_throughput_bottom) {
            indicatorDatasets.push({
              label: `Target bottom ${recipe.category}`,
              type: "line",
              data: [
                {
                  x: Math.min(...props.datasets.flatMap(ds => ds.data.map(p => p.x))),
                  y: recipe.target_throughput_bottom,
                },
                {
                  x: Math.max(...props.datasets.flatMap(ds => ds.data.map(p => p.x))),
                  y: recipe.target_throughput_bottom,
                },
              ],
              borderColor: color,
              borderWidth: 2,
              borderDash: [4, 8],
              pointRadius: 0,
              fill: false,
            });
          }

          // indicatorDatasets.push({
          //   label: `Setting ${recipe.category}`,
          //   type: 'scatter',
          //   data: [{ x: recipe.new_powder_amount_setting, y: recipe.real_throughput }],
          //   borderColor: color,
          //   backgroundColor: color,
          //   pointRadius: 4,
          //   pointHoverRadius: 10,
          //   borderWidth: 3,
          // });
        });
      }

      if (props.powderOutputMeasurements.length) {
        indicatorDatasets.push({
          label: "Measurements",
          type: "scatter",
          data: props.powderOutputMeasurements.map(p => ({ x: p.x, y: p.y })),
          borderColor: "#f64e60",
          backgroundColor: "#f64e60",
          pointRadius: 4,
          pointHoverRadius: 10,
          borderWidth: 3,
        });
      }
      const legendStylePlugin = {
        id: "customLegendIcons",
        afterDraw(chart) {
          const ctx = chart.ctx;
          const legend = chart.legend;

          legend.legendItems.forEach((item, index) => {
            const box = legend.legendHitBoxes[index];
            const centerY = box.top + box.height / 2;

            // Calculate safer positions
            const iconWidth = 15; // short width for neatness
            const paddingRight = 4; // space between line/icon and text label

            const endX = box.left - paddingRight; // right edge closer to text
            const startX = endX - iconWidth; // left edge of icon further away

            ctx.save();

            // Render scatter points as circles
            if (item.datasetType === "scatter") {
              ctx.fillStyle = item.fillStyle;
              ctx.beginPath();
              ctx.arc((startX + endX) / 2, centerY, 4, 0, 2 * Math.PI);
              ctx.fill();
            }
            // Render dashed lines
            else if (item.borderDash && item.borderDash.length) {
              ctx.strokeStyle = item.fillStyle;
              ctx.lineWidth = 2;
              ctx.setLineDash(item.borderDash);
              ctx.beginPath();
              ctx.moveTo(startX, centerY);
              ctx.lineTo(endX, centerY);
              ctx.stroke();
            }
            // Render solid lines
            else {
              ctx.strokeStyle = item.fillStyle;
              ctx.lineWidth = 2;
              ctx.setLineDash([]);
              ctx.beginPath();
              ctx.moveTo(startX, centerY);
              ctx.lineTo(endX, centerY);
              ctx.stroke();
            }

            ctx.restore();
          });
        },
      };

      chartInstance = new Chart(ctx, {
        type: "line",
        data: {
          datasets: [...baseDatasets, ...indicatorDatasets],
        },
        options: {
          responsive: true,
          animation: false,
          interaction: {
            intersect: false,
            mode: "index",
          },
          plugins: {
            tooltip: {
              callbacks: {
                title: contexts => {
                  const xValue = contexts[0].parsed.x;
                  return `x: ${xValue.toFixed(1)}`;
                },
                label: context => {
                  const value = context.raw.y;
                  return `${context.dataset.label}: ${Math.round(value)}`;
                },
              },
            },
            legend: {
              display: true,
              position: useTopLegend.value ? "top" : "right",
              align: useTopLegend.value ? "center" : "start",
              labels: {
                boxWidth: 0,
                usePointStyle: false,
                padding: 30,
                generateLabels: chart => {
                  const datasets = chart.data.datasets;
                  const baseDatasetLabels = props.datasets.map(ds => ds.label);

                  return datasets
                    .map((dataset, index) => ({
                      text: dataset.label,
                      fillStyle: dataset.borderColor,
                      datasetIndex: index,
                      hidden: !chart.isDatasetVisible(index),
                      borderDash: dataset.borderDash || [],
                      lineWidth: dataset.borderWidth || 2,
                      datasetType: dataset.type,
                      isBaseDataset: baseDatasetLabels.includes(dataset.label),
                    }))
                    .filter(item => {
                      if (baseDatasetLabels.length < 2 && item.isBaseDataset) return false;
                      return true;
                    });
                },
              },
              onClick: (_, legendItem) => {
                const index = legendItem.datasetIndex;
                chartInstance.setDatasetVisibility(index, !chartInstance.isDatasetVisible(index));
                chartInstance.update();
              },
            },
          },
          scales: {
            x: {
              type: "linear",
              title: { display: true, text: props.xTitle },
              grid: { drawBorder: false, borderDash: [5, 5] },
              ticks: { color: "#9ca2b7", padding: 10 },
            },
            y: {
              title: { display: true, text: props.yTitle },
              grid: { drawBorder: false, borderDash: [5, 5] },
              ticks: { color: "#9ca2b7", padding: 10 },
            },
          },
        },
        plugins: [legendStylePlugin],
      });
    };

    watch([() => props.datasets, () => props.recipes, () => props.powderOutputMeasurements], drawChart, {
      deep: true,
      immediate: true,
    });

    onMounted(drawChart);
    onBeforeUnmount(() => chartInstance && chartInstance.destroy());

    const showAll = () => {
      chartInstance.data.datasets.forEach((_, i) => chartInstance.setDatasetVisibility(i, true));
      chartInstance.update();
    };

    const hideAll = () => {
      chartInstance.data.datasets.forEach((_, i) => chartInstance.setDatasetVisibility(i, false));
      chartInstance.update();
    };

    return {
      chartCanvas,
      showAll,
      hideAll,
      useTopLegend,
    };
  },
};
</script>

<style scoped>
.chart-layout {
  display: flex;
  flex-direction: column;
}

.controls {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 12px;
}

.legend-btn {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #f1f4f9;
  border: 1px solid #ced4da;
  color: #495057;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.legend-btn:hover {
  background-color: #e2e6ea;
}

canvas {
  max-height: 500px;
  flex-grow: 1;
}
</style>
